import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { OdataResponse } from '../../interfaces/online-only.interface';
import { CategoryModel } from '../../models/complex/internal-info/category.model';
import { InternalInfoModel } from '../../models/complex/internal-info/internal-info.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { NotificationModel, ODataNotificationModel } from '../../models/low-level/notification.model';
import { OdataService } from '../../services/odata/odata.service';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { isDefined } from '../../utilities/is-defined';
import { ODataUtilities } from '../../utilities/odata-utilities';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { UserMasterService } from '../user-master/user-master.service';
import { OdataNotificationServiceInterface } from './odata-notification.interface';
import {
    NotificationHistoryModel,
    ODataNotificationHistoryModel,
} from '../../models/low-level/notification-history.model';

@Injectable({
    providedIn: 'root',
})
export class OdataNotificationService implements OdataNotificationServiceInterface {
    public incorrectNotificationsList: string[];
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly http: HttpClient,
        private readonly userMasterService: UserMasterService,
        private readonly odataService: OdataService,
    ) {}

    public getNotifications(): Observable<NotificationModel[]> {
        this.incorrectNotificationsList = [];
        let query = this.userMasterService.setSalesOrgAndPlantForUrl(
            this.uriUtilities.getUriForContent() + ENDPOINTS.NotificationHeaders,
        );
        const expand = this.uriUtilities.getNotificationHeaderByAppId();

        query = this.uriUtilities.appendParameter(query, expand);

        return this.http.get<OdataResponse<ODataNotificationModel>>(query).pipe(
            map((response) =>
                ODataUtilities.transformResponseDataToArray(response.d)
                    .map((n) => {
                        try {
                            return new NotificationModel(n);
                        } catch (e) {
                            this.log.error(
                                `OdataNotificationService: getNotifications : new NotificationModel failed for ${
                                    n.NotificationNo
                                }:${n.TaskNumber} due to ${JSON.stringify(e)}`,
                            );
                            this.incorrectNotificationsList.push(`${n.NotificationNo}:${n.TaskNumber}`);
                        }
                    })
                    .filter(isDefined),
            ),
            catchError((err) => {
                this.log.error('getNotifications - request error: ' + JSON.stringify(err));
                return throwError(() => err);
            }),
        );
    }

    public getNotificationsHistory(numberOfDays: number): Observable<NotificationHistoryModel[]> {
        let query = this.userMasterService.setSalesOrgAndPlantForUrl(
            this.uriUtilities.getUriForContent() + ENDPOINTS.NotificationHistory,
        );
        const filter = `NumberOfDays eq '${numberOfDays}'`;
        const format = '$format=json';

        query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, format);

        return this.http.get<OdataResponse<ODataNotificationHistoryModel>>(query).pipe(
            map((response) =>
                ODataUtilities.transformResponseDataToArray(response.d)
                    .map((notification) => {
                        try {
                            return new NotificationHistoryModel(notification);
                        } catch (e) {
                            this.log.error(
                                `OdataNotificationService: getNotificationsHistory : new NotificationModel failed for ${
                                    notification.NotificationNo
                                }:${notification.TaskNumber} due to ${JSON.stringify(e)}`,
                            );
                        }
                    })
                    .filter(isDefined),
            ),
            catchError((err) => {
                this.log.error('getNotificationsHistory - request error: ' + JSON.stringify(err));
                return throwError(() => err);
            }),
        );
    }

    public sendInternalInfoModel(
        internalInfoModel: InternalInfoModel | CategoryModel,
        notificationId: NotificationIdentifierModel,
    ): Observable<unknown> {
        return this.odataService.deepInsertRequest(
            internalInfoModel,
            ENDPOINTS.InternalInfoSubmitSet,
            `${notificationId.toString()}-${getUuidV4()}`,
        );
    }
}
